// =============================================================================
// SimSense Platform — Zustand Store
// =============================================================================

import { create } from "zustand";
import { api } from "../utils/api";

interface Session {
  id: string;
  subsessionId: string;
  trackName: string;
  carName: string;
  sessionDate: string;
  driverName: string;
  eventType: string;
  carClass?: string;
  season?: string;
  weather?: Record<string, any>;
  metadata?: Record<string, any>;
  // OT-208: Quick stats from subquery
  lapCount?: number;
  bestLapTime?: number | null;
  stintCount?: number;
}

interface TelemetryState {
  sessions: Session[];
  isLoading: boolean;
  error: string | null;
  uploadStatus: string | null;

  fetchSessions: () => Promise<void>;
  uploadFile: (file: File) => Promise<string>;
  pollJobStatus: (jobId: string) => Promise<{ status: string; error?: string }>;
  deleteSession: (id: string) => Promise<void>;
}

export const useStore = create<TelemetryState>((set, get) => ({
  sessions: [],
  isLoading: false,
  error: null,
  uploadStatus: null,

  fetchSessions: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await api<Session[]>("/api/sessions");
      set({ sessions: data, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  pollJobStatus: async (jobId: string) => {
    const maxWait = 300000; // 5 minutes
    const interval = 2000;  // 2 seconds
    const start = Date.now();

    while (Date.now() - start < maxWait) {
      try {
        const resp = await fetch(`/api/jobs/${jobId}`, { credentials: "include" });
        if (!resp.ok) break;
        const data = await resp.json();

        if (data.status === "complete") {
          return { status: "complete" };
        }
        if (data.status === "failed") {
          return { status: "failed", error: data.error || "Processing failed" };
        }

        // Still processing — update status
        set({ uploadStatus: `Processing: ${data.status}` });
      } catch {
        // Network blip — keep polling
      }
      await new Promise((r) => setTimeout(r, interval));
    }
    return { status: "timeout", error: "Processing timed out" };
  },

  uploadFile: async (file: File) => {
    set({ isLoading: true, error: null, uploadStatus: "Uploading..." });
    try {
      const formData = new FormData();
      formData.append("file", file);

      let resp = await fetch("/api/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      // Auto-refresh on 401 and retry
      if (resp.status === 401) {
        const refreshResp = await fetch("/api/ss/auth/refresh", {
          method: "POST",
          credentials: "include",
        });
        if (refreshResp.ok) {
          const retryForm = new FormData();
          retryForm.append("file", file);
          resp = await fetch("/api/upload", {
            method: "POST",
            credentials: "include",
            body: retryForm,
          });
        }
      }

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
        throw new Error(errorData.detail || "Upload failed");
      }

      const data = await resp.json();
      const jobId = data.jobId;

      if (!jobId) {
        // No job ID — fallback to delayed refresh
        set({ isLoading: false, uploadStatus: null });
        setTimeout(() => get().fetchSessions(), 5000);
        return "";
      }

      // Poll for job completion
      set({ uploadStatus: "Processing telemetry data..." });
      const result = await get().pollJobStatus(jobId);

      if (result.status === "complete") {
        set({ isLoading: false, uploadStatus: null });
        await get().fetchSessions();
      } else {
        set({
          isLoading: false,
          uploadStatus: null,
          error: result.error || "Processing failed",
        });
      }

      return jobId;
    } catch (err: any) {
      set({ error: err.message, isLoading: false, uploadStatus: null });
      throw err;
    }
  },

  deleteSession: async (id: string) => {
    try {
      await api(`/api/sessions/${id}`, { method: "DELETE" });
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== id),
      }));
    } catch (err: any) {
      console.error("Failed to delete session", err);
      throw err;
    }
  },
}));
